import { skipToken } from "@reduxjs/toolkit/query";
import { RESEARCH_AGENT_ID } from "@repo/app-types/entities/researchAgent";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { ChatComposer } from "@repo/ui/chat/chatComposer";
import { ChatEmptyState } from "@repo/ui/chat/chatEmptyState";
import { ChatHeader, chatHeaderIconClassName } from "@repo/ui/chat/chatHeader";
import {
  ChatMessage,
  type ChatMessageRenderTextArgs,
} from "@repo/ui/chat/chatMessage";
import { ChatThread } from "@repo/ui/chat/chatThread";
import { ChatThreadSkeleton } from "@repo/ui/chat/chatThreadSkeleton";
import { messageItemStyle } from "@repo/ui/chat/messageItem";
import { MessageThinkingIndicator } from "@repo/ui/chat/messageThinkingIndicator";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { GradingReferenceProvider } from "@repo/ui/structuredContent/gradingReferenceContext";
import { ModelOutputContent } from "@repo/ui/structuredContent/modelOutputContent";
import * as Sentry from "@sentry/react";
import { IconAlertTriangle, IconPlus, IconX } from "@tabler/icons-react";
import { useLayoutEffect, useMemo, useRef, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { useMastraThreadQuery } from "../../api/entities/mastraThread.js";
import { buildInteractionResolverFromMessages } from "../agents/interactionResolver.js";
import { useMastraChatContext } from "../agents/mastra/mastraChatContext.js";
import { CountCustomerInteractionsTool } from "../agents/tools/countCustomerInteractionsTool.js";
import { FallbackTool } from "../agents/tools/fallbackTool.js";
import { GetCriterionDetailsTool } from "../agents/tools/getCriterionDetailsTool.js";
import { ListCustomerInteractionsTool } from "../agents/tools/listCustomerInteractionsTool.js";
import { ResearchCustomerInteractionsTool } from "../agents/tools/researchCustomerInteractionsTool.js";
import { ResearchTeammatesTool } from "../agents/tools/researchTeammatesTool.js";
import { RetrieveCoachingNotesTool } from "../agents/tools/retrieveCoachingNotesTool.js";
import { SearchTeammatesTool } from "../agents/tools/searchTeammatesTool.js";
import { GradingReferenceLinkAdapter } from "../teammatePerformance/gradingReferenceLinkAdapter.js";
import { ResearchAgentChatMenu } from "./researchAgentChatMenu.js";
import {
  FALLBACK_THREAD_TITLE,
  researchAgentThreadMetadataSchema,
  type ResearchAgentProps,
} from "./researchAgentContext.js";
import { ResearchAgentContextPill } from "./researchAgentContextPill.js";
import { useResearchAgentSuggestions } from "./researchAgentSuggestions.js";

type ResearchAgentChatSurface = "panel" | "modal";

export interface ResearchAgentChatViewProps {
  researchAgent: ResearchAgentProps;
  /**
   * `panel` renders the compact side-panel chat; `modal` renders the expanded
   * chat column with its content centered on a wide rail.
   */
  surface: ResearchAgentChatSurface;
  onRequestClose?: () => void;
  /** Panel-only: switch the panel to the history slide-over. */
  onShowHistory?: () => void;
  /** Panel-only: open the expanded modal. */
  onRequestExpand?: () => void;
}

const modalContentRailClassName = "mx-auto w-full max-w-[760px]";
const modalEmptyStateRailClassName = "mx-auto w-full max-w-[680px]";

/**
 * The research agent conversation: header, message thread, and composer. The
 * same view drives the docked side panel and the expanded modal.
 */
export const ResearchAgentChatView: FC<ResearchAgentChatViewProps> = (
  props,
) => {
  const {
    resourceId,
    threadId,
    threadTitle,
    threadResourceId,
    threadMetadata,
    isThreadNotFound,
  } = props.researchAgent;

  const isModalSurface = props.surface === "modal";
  const isNewThread = threadId === undefined;
  const isOwnThread = threadResourceId === resourceId;

  // Get the Mastra thread corresponding to the selected thread ID.
  // `currentData` rather than `data`: the latter holds on to the previously
  // selected thread until the new one's fetch settles, which would flash that
  // thread's title in the header and apply its share state to this one.
  const { currentData: currentThread } = useMastraThreadQuery(
    threadId !== undefined
      ? { agentId: RESEARCH_AGENT_ID, threadId }
      : skipToken,
  );

  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);

  const chat = useMastraChatContext();
  const lastMessageId = chat.messages.at(-1)?.id;
  const isEmpty = chat.messages.length === 0 && !chat.isLoading;

  // The modal's regular empty state renders the composer inline; every other
  // state (including "chat not found", which has no inline composer) keeps
  // the pinned one.
  const showsPinnedComposer = !(
    isModalSurface &&
    isOwnThread &&
    isEmpty &&
    !isThreadNotFound
  );

  const focusComposer = useEffectEvent(() => {
    const composerInput = composerInputRef.current;
    composerInput?.focus({ preventScroll: true });
    if (document.activeElement === composerInput) {
      return undefined;
    }

    // The focus didn't take, which means an ancestor is still `inert`: the
    // panel clears that when its own ref attaches, and a parent's refs attach
    // after its children's layout effects run. Retry once the commit is done.
    const frame = requestAnimationFrame(() =>
      composerInput?.focus({ preventScroll: true }),
    );
    return () => cancelAnimationFrame(frame);
  });

  // Starting a new chat is the only thing that moves focus here by itself.
  // Comparing against the request the view was built with (rather than a
  // "have I mounted yet" flag) keeps StrictMode's double-invoked effects from
  // counting as a request, so opening a page never steals the user's
  // keystrokes.
  const { composerFocusRequestId } = props.researchAgent;
  const handledFocusRequestIdRef = useRef(composerFocusRequestId);
  useLayoutEffect(() => {
    if (handledFocusRequestIdRef.current === composerFocusRequestId) {
      return undefined;
    }

    handledFocusRequestIdRef.current = composerFocusRequestId;
    return isOwnThread ? focusComposer() : undefined;
  }, [composerFocusRequestId, isOwnThread, focusComposer]);

  // Sending the first message moves the modal's composer out of the empty
  // state and into the footer, destroying the focused textarea. Left alone,
  // the dialog's focus scope hands focus to the header's New chat button,
  // which paints a focus ring there. This only recovers focus the swap
  // orphaned — if it went anywhere else, it stays there.
  const previousShowsPinnedComposerRef = useRef(showsPinnedComposer);
  useLayoutEffect(() => {
    const didComposerMove =
      previousShowsPinnedComposerRef.current !== showsPinnedComposer;
    previousShowsPinnedComposerRef.current = showsPinnedComposer;

    if (!didComposerMove || document.activeElement !== document.body) {
      return undefined;
    }

    return focusComposer();
  }, [showsPinnedComposer, focusComposer]);

  // Falling back through the title the thread was selected with keeps the
  // header on the right name while its own fetch is in flight. An unsent
  // thread and one still awaiting its generated title share the last fallback,
  // so the header holds steady until the real title lands.
  const headerTitle =
    currentThread?.title || threadTitle || FALLBACK_THREAD_TITLE;

  // The freshly fetched thread has the latest share state; validate it rather
  // than trusting the loosely-typed Mastra metadata.
  const currentThreadMetadataParse =
    researchAgentThreadMetadataSchema.safeParse(currentThread?.metadata);
  const currentThreadMetadata = currentThreadMetadataParse.success
    ? currentThreadMetadataParse.data
    : undefined;

  const suggestions = useResearchAgentSuggestions();
  const handleSuggestionSelect = (prompt: string) => {
    props.researchAgent.onNewThreadSelect({ initialUserMessageText: prompt });
  };

  const footerClassName = twMerge(
    "shrink-0 bg-card",
    isModalSurface ? "px-6 pt-3 pb-6" : "px-3 pt-2 pb-3",
  );

  const composer = (
    <ChatComposer
      variant="boxed"
      inputRef={composerInputRef}
      placeholder="Ask a question..."
      // Naming the thread's scope, which is the context the agent is given
      // whether or not the user attaches anything themselves.
      contextPills={
        <ResearchAgentContextPill
          threadMetadata={currentThreadMetadata ?? threadMetadata}
        />
      }
      isRunning={chat.isRunning}
      onStop={chat.stop}
      onSend={chat.sendMessage}
      // Controlled from the provider: the panel and the modal never render at
      // once, so an internally-held draft would be lost moving between them.
      value={props.researchAgent.composerDraft}
      onValueChange={props.researchAgent.onComposerDraftChange}
    />
  );

  const emptyState = isThreadNotFound ? (
    <ThreadNotFoundState />
  ) : isModalSurface && isOwnThread ? (
    // In the modal the empty state centers vertically, with the composer
    // inline underneath the suggestions instead of pinned to the bottom.
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-5">
      <div className={twMerge("flex flex-col", modalEmptyStateRailClassName)}>
        <ChatEmptyState
          suggestions={suggestions}
          onSuggestionSelect={handleSuggestionSelect}
        />
        <div className="mt-8">{composer}</div>
      </div>
    </div>
  ) : (
    <div className="flex flex-1 flex-col items-center px-3 pt-[clamp(5rem,18vh,9rem)] pb-5">
      <ChatEmptyState
        className="w-[calc(100%-1.5rem)] max-w-[420px]"
        suggestions={isOwnThread ? suggestions : []}
        onSuggestionSelect={handleSuggestionSelect}
      />
    </div>
  );

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden bg-card">
      <ChatThread
        className="min-h-0 bg-card"
        messages={chat.messages}
        isLoading={chat.isLoading}
        loadingIndicator={
          <ChatThreadSkeleton
            className={isModalSurface ? modalContentRailClassName : undefined}
          />
        }
        showsEdgeFades
        timelineClassName={isModalSurface ? "px-6 py-5" : "px-3 py-2"}
        header={
          <ChatHeader
            className={
              isModalSurface
                ? "border-b border-border-light bg-card px-6 py-4"
                : "bg-card px-3 pt-3 pb-2"
            }
            title={headerTitle}
            titleAccessory={
              !isOwnThread ? (
                <div className="shrink-0 rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                  View only
                </div>
              ) : null
            }
            actions={
              <>
                {isModalSurface && !isNewThread ? (
                  <IconButton
                    accessibleLabel="New chat"
                    variant={ButtonVariant.Tertiary}
                    icon={<IconPlus className={chatHeaderIconClassName} />}
                    onClick={() => props.researchAgent.onNewThreadSelect()}
                  />
                ) : null}

                <ResearchAgentChatMenu
                  researchAgent={props.researchAgent}
                  threadMetadata={currentThreadMetadata ?? threadMetadata}
                  showsNewChatItem={!isModalSurface}
                  onShowHistory={props.onShowHistory}
                  onRequestExpand={props.onRequestExpand}
                />

                {props.onRequestClose ? (
                  <IconButton
                    accessibleLabel="Close research assistant"
                    variant={ButtonVariant.Tertiary}
                    icon={<IconX className={chatHeaderIconClassName} />}
                    onClick={props.onRequestClose}
                  />
                ) : null}
              </>
            }
          />
        }
        emptyState={emptyState}
        // Turns break wider than the parts within a turn. Without the inner
        // gap, an assistant turn's text and tool-call rows stack flush: the
        // bubble backgrounds that used to separate them are gone on this
        // surface, so spacing has to do that work.
        messagesClassName="gap-4"
        renderMessage={(message) => (
          <div
            className={twMerge(
              // `space-y` rather than a flex gap: flex items default to
              // `min-width: auto`, which stops long markdown (code blocks,
              // tables, URLs) from shrinking and overflows the panel.
              "max-w-full min-w-0 space-y-3 break-words [&_a]:break-all [&_pre]:max-w-full [&_pre]:whitespace-pre-wrap",
              isModalSurface && modalContentRailClassName,
            )}
          >
            <Sentry.ErrorBoundary fallback={<AssistantMessageError />}>
              <ChatMessage
                message={message}
                isStreaming={chat.isRunning && message.id === lastMessageId}
                tools={TOOL_COMPONENTS}
                renderText={renderResearchText}
                renderTypingIndicator={() => <MessageThinkingIndicator />}
              />
            </Sentry.ErrorBoundary>
          </div>
        )}
        composer={
          !isOwnThread ? (
            <div
              className={twMerge(
                footerClassName,
                "text-center text-sm text-muted-foreground",
              )}
            >
              This chat is view-only. You cannot send messages.
            </div>
          ) : showsPinnedComposer ? (
            <div className={footerClassName}>
              <div
                className={
                  isModalSurface ? modalContentRailClassName : undefined
                }
              >
                {composer}
              </div>
            </div>
          ) : undefined
        }
      />
    </div>
  );
};

const ThreadNotFoundState: FC = () => (
  <div className="flex flex-1 flex-col items-center justify-center p-5">
    <div className="flex flex-col items-center gap-4">
      <IconAlertTriangle className="size-12 text-gray-400" />
      <div className="text-center">
        <h2 className="mb-1 text-base font-semibold text-foreground">
          Chat not found
        </h2>
        <p className="text-sm text-muted-foreground">
          This chat doesn&apos;t exist or you don&apos;t have access to it.
        </p>
      </div>
    </div>
  </div>
);

const TOOL_COMPONENTS = {
  byName: {
    searchTeammatesTool: SearchTeammatesTool,
    researchTeammatesTool: ResearchTeammatesTool,
    countCustomerInteractionsTool: CountCustomerInteractionsTool,
    listCustomerInteractionsTool: ListCustomerInteractionsTool,
    researchCustomerInteractionsTool: ResearchCustomerInteractionsTool,
    getCriterionDetailsTool: GetCriterionDetailsTool,
    retrieveCoachingNotesTool: RetrieveCoachingNotesTool,
  },
  Fallback: FallbackTool,
};

// The research agent uses the `agent` layout it shares with the configuration
// assistants: a muted user bubble above naked assistant text, both
// left-aligned and shrinking to fit. (Simulations keep `simulation`, whose
// right-aligned `max-w-[80%]` bubbles suit a transcript of a two-party
// conversation.)
// Both roles render structured content, which styles its own elements, so
// neither bubble opts into the `prose` variant.
const userStyle = messageItemStyle({ role: "user", layout: "agent" });
const assistantStyle = messageItemStyle({
  role: "assistant",
  layout: "agent",
});

/** Renders a research-agent text part using the `agent` layout. */
function renderResearchText({
  text,
  role,
  isStreaming,
}: ChatMessageRenderTextArgs) {
  // User prompts go through the same structured content pipeline as the
  // assistant's text, for consistency; user input is about as trusted as model
  // output.
  const style = role === "user" ? userStyle : assistantStyle;
  return (
    <div className={style.row()}>
      <div className={style.bubble()}>
        <ResearchMessageTextContent text={text} isStreaming={isStreaming} />
      </div>
    </div>
  );
}

const ResearchMessageTextContent: FC<{
  text: string;
  isStreaming: boolean;
}> = ({ text, isStreaming }) => {
  const chat = useMastraChatContext();

  // Quotes and references cite interactions returned by earlier tool calls in
  // the thread, so resolve them against those tool results.
  const resolveInteractionId = useMemo(
    () => buildInteractionResolverFromMessages(chat.messages),
    [chat.messages],
  );

  return (
    <GradingReferenceProvider value={GradingReferenceLinkAdapter}>
      <ModelOutputContent
        text={text}
        isStreaming={isStreaming}
        resolveInteractionId={resolveInteractionId}
      />
    </GradingReferenceProvider>
  );
};

const AssistantMessageError: FC = () => (
  <div
    className={messageItemStyle({
      role: "assistant",
      layout: "agent",
      isError: true,
    }).bubble({ className: "px-3.5" })}
  >
    Unable to render this message.
  </div>
);
